// Vorlagen-Service für Angebots-Textbausteine.
//
// Quelle der Wahrheit: der SERVER (db.quote_templates, pro Account/Tenant).
// localStorage ist nur noch ein Lese-Cache, damit alle Aufrufer synchron
// bleiben können. Jede Änderung wird sofort zum Server gepusht.
// (Vorher lagen die Vorlagen nur im Browser + als Defaults im Bundle —
//  d.h. für jeden Account identisch und nicht geräteübergreifend.)

const TEMPLATES_KEY = 'quote_templates';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getAuthToken() {
  return localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
}

let syncTimer = null;
let serverLoaded = false;

class TemplateService {
  // Vorlagen vom Server in den Cache laden (einmal pro App-Start reicht;
  // läuft lazy beim ersten getTemplates() an).
  static async syncFromServer(force = false) {
    if (serverLoaded && !force) return;
    const token = getAuthToken();
    if (!token || !BACKEND_URL) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/quote-templates/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const templates = await res.json();
      serverLoaded = true;
      localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
      window.dispatchEvent(new CustomEvent('templatesUpdated', { detail: templates }));
    } catch (error) {
      console.error('Vorlagen-Sync fehlgeschlagen:', error);
    }
  }

  // Cache-Stand zum Server pushen (debounced, kompletter Satz)
  static pushToServer() {
    const token = getAuthToken();
    if (!token || !BACKEND_URL) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(async () => {
      try {
        const stored = localStorage.getItem(TEMPLATES_KEY);
        const templates = stored ? JSON.parse(stored) : [];
        await fetch(`${BACKEND_URL}/api/quote-templates/bulk`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ templates }),
        });
      } catch (error) {
        console.error('Vorlagen-Push fehlgeschlagen:', error);
      }
    }, 800);
  }

  // Get all templates (aus dem Cache; stößt lazy den Server-Sync an)
  static getTemplates() {
    try {
      if (!serverLoaded) this.syncFromServer();
      const stored = localStorage.getItem(TEMPLATES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading templates:', error);
      return [];
    }
  }

  // Save templates (Cache + Server)
  static saveTemplates(templates) {
    try {
      localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
      window.dispatchEvent(new CustomEvent('templatesUpdated', { detail: templates }));
      this.pushToServer();
      return true;
    } catch (error) {
      console.error('Error saving templates:', error);
      return false;
    }
  }

  // Add single template
  static addTemplate(template) {
    const templates = this.getTemplates();
    const newTemplate = {
      ...template,
      id: template.id || Date.now().toString(),
      createdAt: template.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    templates.push(newTemplate);
    this.saveTemplates(templates);
    return newTemplate;
  }

  // Update template
  static updateTemplate(id, updates) {
    const templates = this.getTemplates();
    const index = templates.findIndex(t => t.id === id);
    if (index === -1) return null;

    templates[index] = {
      ...templates[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    this.saveTemplates(templates);
    return templates[index];
  }

  // Delete template
  static deleteTemplate(id) {
    const templates = this.getTemplates();
    const filtered = templates.filter(t => t.id !== id);
    this.saveTemplates(filtered);
    return true;
  }

  // Get template by ID
  static getTemplate(id) {
    const templates = this.getTemplates();
    return templates.find(t => t.id === id);
  }

  // Get templates by type
  static getTemplatesByType(type) {
    const templates = this.getTemplates();
    return templates.filter(t => t.type === type);
  }

  // Subscribe to template changes
  static subscribe(callback) {
    const handler = (event) => callback(event.detail);
    window.addEventListener('templatesUpdated', handler);
    return () => window.removeEventListener('templatesUpdated', handler);
  }

  // Export templates
  static exportTemplates() {
    const templates = this.getTemplates();
    const dataStr = JSON.stringify(templates, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vorlagen-export-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  // Import templates
  static importTemplates(file, merge = true) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          if (!Array.isArray(imported)) {
            reject(new Error('Invalid file format'));
            return;
          }

          if (merge) {
            const existing = this.getTemplates();
            const merged = [...existing];
            imported.forEach(template => {
              if (!merged.find(t => t.id === template.id)) {
                merged.push(template);
              }
            });
            this.saveTemplates(merged);
            resolve(imported.length);
          } else {
            this.saveTemplates(imported);
            resolve(imported.length);
          }
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }
}

export default TemplateService;
